import { parse, isValid } from 'date-fns';
import { performGetRequest } from '@atlassian/jira-fetch/src/index.tsx';
import { formatWithLocale } from '@atlassian/jira-platform-utils-date-fns/src/main.tsx';

const JSM_CONTEXT_URL = '/rest/servicedesk/latest/jsmcontext';

// The Packaging GP enforcement date is October 16, 2025.
const PACKAGING_GP_ENFORCEMENT_DATE = new Date('2025-10-16');

type BillingPeriod = 'MONTHLY' | 'ANNUAL';

type GpEndDateDetails = {
	maintenanceEndDate: Date;
	billingPeriod: BillingPeriod;
};

const dateHasExpectedFormat = (date: string) => {
	const dateFormat = 'yyyy-MM-dd';
	const parsedDate = parse(date, dateFormat, new Date());

	return isValid(parsedDate);
};

export const getGpEndDateDetails = async (): Promise<GpEndDateDetails> => {
	const data = await performGetRequest(JSM_CONTEXT_URL);

	if (!dateHasExpectedFormat(data.maintenanceEndDate)) {
		throw new Error(`Invalid maintenance end date, received: ${data.maintenanceEndDate}`);
	}

	if (data.billingPeriod !== 'MONTHLY' && data.billingPeriod !== 'ANNUAL') {
		throw new Error(`Invalid billing period, received: ${data.billingPeriod}`);
	}

	return {
		maintenanceEndDate: new Date(data.maintenanceEndDate),
		billingPeriod: data.billingPeriod,
	};
};

const getMonthlyBillingGPEndDate = (date: Date): Date => {
	const currentBillingDate = date;
	while (currentBillingDate < PACKAGING_GP_ENFORCEMENT_DATE) {
		currentBillingDate.setMonth(currentBillingDate.getMonth() + 1);
	}
	return currentBillingDate;
};

const getAnnualBillingGPEndDate = (date: Date): Date => {
	const currentBillingDate = date;
	while (currentBillingDate < PACKAGING_GP_ENFORCEMENT_DATE) {
		currentBillingDate.setFullYear(currentBillingDate.getFullYear() + 1);
	}
	return currentBillingDate;
};

export const calculateGpEndDate = (
	maintenanceEndDate: Date,
	billingPeriod: BillingPeriod,
	locale: string,
) => {
	if (billingPeriod === 'MONTHLY') {
		return formatWithLocale(getMonthlyBillingGPEndDate(maintenanceEndDate), 'MMM dd, yyyy', locale);
	}

	if (billingPeriod === 'ANNUAL') {
		return formatWithLocale(getAnnualBillingGPEndDate(maintenanceEndDate), 'MMM dd, yyyy', locale);
	}
};
